import React, { useCallback, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { Search } from 'baseui/icon';
import { Input } from 'baseui/input';
import { COLOR } from 'baseui/badge';
import { AgentAvatar } from '../agent-avatar/AgentAvatar';
import { Button, KIND, SIZE } from 'baseui/button';
import { CustomTheme } from '../baseweb-custom-theme/customTheme';
import { useCustomerInfo } from '../../context/CustomerInfoContext';
import {
  TwoWayService,
  getUpdatedDate,
} from '../relay-messenger-axios-service/relay-messenger-axios-service';
import { useClientInfo } from '../../context/ClientInfoContext';
import { Badge } from '../badge/Badge';
import { useUserInfo } from '../../context/UserInfoContext';
import { AgentStatus } from '../filter-sorting/AgentFilterSortingPopover';
import { useAssignedAgents } from '../../context/AssignedAgentsContext';
import { AgentStatusT } from '../relay-messenger-axios-service/relay-messenger-axios-service';

export type RelayMessengerAgent = {
  id: string;
  roleId: string[];
  clientId: string;
  emailAddress: string;
  fullName: string;
  firstName: string;
  lastName: string;
  lastLogin: string | null;
  groupId: string | null;
  assignedConversation?: number;
  imageURL?: string;
  isSelected?: boolean;
  status?: AgentStatusT;
};

export type AgentModalVariant = 'assign' | 'invite' | 're-assign';

export type AgentModalProps = {
  customerCcid: string;
  isInviteModalOpen: boolean;
  setIsInviteCheckModalOpen: (value: boolean) => void;
  setSelectedAgent: (agent: RelayMessengerAgent) => void;
  closeModal: () => void;
  variant?: AgentModalVariant;
  // when set, searches for agents eligible for ALL of these tags instead of
  // the single selected conversation's tag (Unassigned multiselect "Assign" flow)
  tagIds?: string[];
  conversationCount?: number;
};

type TextMessageProps = {
  message: string;
};

const TextMessage = ({ message }: TextMessageProps) => {
  const [css] = useStyletron();
  return (
    <p
      className={css({
        fontSize: '14px',
        fontWeight: '600',
        lineHeight: '15.6px',
        marginTop: '20px',
        marginBottom: '20px',
        marginLeft: '12px',
        marginRight: '12px',
      })}
    >
      {message}
    </p>
  );
};

const AgentCountMessage = ({ message }: TextMessageProps) => {
  const [css] = useStyletron();
  return (
    <p
      className={css({
        fontSize: '12px',
        lineHeight: '10.06px',
        fontWeight: '600',
        color: '#37474F80',
      })}
    >
      {message}
    </p>
  );
};

export const AgentModal = ({
  customerCcid,
  isInviteModalOpen,
  setIsInviteCheckModalOpen,
  setSelectedAgent,
  closeModal,
  variant = 'invite',
  tagIds,
  conversationCount,
}: AgentModalProps) => {
  const [css, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  const { customerInfo } = useCustomerInfo();
  const { firstName, lastName, dateCreated, dateUpdated, tagName } =
    customerInfo;
  // snapshot the tag id(s) for the life of this modal: both CustomerInfoContext
  // and the bulk-select tagIds prop are recomputed in the background by the
  // conversations poll, and re-reading them live would reset the agent search
  // (and flash "Loading...") whenever something unrelated updates them
  const [tagId] = useState(() => customerInfo.tagId);
  const [frozenTagIds] = useState(() => tagIds);
  const [{ clientId }] = useClientInfo();
  const [{ userId }] = useUserInfo();
  const formattedDateCreated = new Date(dateCreated).toLocaleDateString();
  const updatedDate = dateUpdated ? getUpdatedDate(dateUpdated) : '';
  const [searchText, setSearchText] = useState('');
  const [agents, setAgents] = useState<RelayMessengerAgent[]>([]);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [isAgentsError, setIsAgentsError] = useState(false);
  const { assignedAgents } = useAssignedAgents();
  const isBulkTagSearch = frozenTagIds && frozenTagIds.length > 0;

  const fetchAgents = useCallback(async () => {
    // perform search when invite/assign/reassigned modals are open
    try {
      const agents = isBulkTagSearch
        ? await TwoWayService.getAgentsByTags(clientId, frozenTagIds, searchText)
        : await TwoWayService.getAgentsByTag(clientId, tagId, searchText);
      // exclude the current user from the list of agents only if the variant is not re-assign
      let updatedAgents: RelayMessengerAgent[] = agents;
      if (variant !== 're-assign') {
        updatedAgents = agents?.filter((agent) => agent.id !== userId);
      }
      setAgents(updatedAgents);
    } catch (error) {
      if (error.response?.data?.error === 'no agents found') {
        setAgents([]);
      } else {
        setIsAgentsError(true);
      }
    } finally {
      setIsAgentsLoading(false);
    }
  }, [isInviteModalOpen, tagId, isBulkTagSearch, frozenTagIds, searchText]);

  useEffect(() => {
    setIsAgentsLoading(true);
    setIsAgentsError(false);
    // debounce search to reduce the number of requests
    const timeoutId = setTimeout(() => {
      if (isInviteModalOpen && (isBulkTagSearch || tagId)) {
        fetchAgents();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText, isInviteModalOpen, tagId, isBulkTagSearch, frozenTagIds]);

  const selectAgent = (agent: RelayMessengerAgent) => {
    closeModal();
    setIsInviteCheckModalOpen(true);
    setSelectedAgent(agent);
  };

  return (
    <div>
      <p
        className={css({
          color: '#37474F33',
          fontWeight: '600',
          lineHeight: '15.6px',
          textTransform: 'uppercase',
          fontSize: '14px',
        })}
      >
        Conversation
      </p>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '22px',
          paddingRight: '22px',
        })}
      >
        {isBulkTagSearch ? (
          <p
            className={css({
              fontSize: '17px',
              lineHeight: '13.8px',
              fontWeight: '700',
            })}
          >
            {conversationCount} conversation
            {conversationCount === 1 ? '' : 's'} selected
          </p>
        ) : (
          <>
            <p
              className={css({
                fontSize: '17px',
                lineHeight: '13.8px',
                fontWeight: '700',
              })}
            >
              {firstName} {lastName}
            </p>
            <p
              className={css({
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '15.6px',
              })}
            >
              Account ID (CCID): {customerCcid}
            </p>
            <div
              className={css({
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                fontSize: '14px',
                fontWeight: '400',
                lineHeight: '15.6px',
              })}
            >
              <p>Created: {formattedDateCreated}</p>
              <p>Last Modified: {updatedDate}</p>
              <p>Tag: {tagName}</p>
            </div>
          </>
        )}
      </div>
      <div
        className={css({
          display: 'flex',
          padding: '0px 12px',
          flexDirection: 'row',
        })}
      >
        <Input
          startEnhancer={<Search size="18px" />}
          placeholder="Search by agent name or email"
          value={searchText}
          type="text"
          onChange={(e) => setSearchText(e.target['value'])}
        />
      </div>
      {/* Load two-way agents */}
      {isAgentsLoading ? (
        <TextMessage message="Loading..." />
      ) : isAgentsError ? (
        <TextMessage message="There was an error fetching agents. Please refresh to try again!" />
      ) : agents.length ? (
        <div
          className={css({
            display: 'flex',
            padding: '0px 12px',
            flexDirection: 'column',
            marginTop: '20px',
          })}
        >
          <AgentCountMessage
            message={`${agents.length} agent${agents.length !== 1 ? 's' : ''}`}
          />
          <div
            className={css({
              maxHeight: '42vh',
              overflowY: 'auto',
              marginBottom: '20px',
            })}
          >
            {agents.map((agent, index) => {
              const disabledButtonAssignedAgent = assignedAgents.find(
                (assignedAgent) =>
                  assignedAgent['attributes']['agent'].id === agent.id,
              );
              return (
                <div
                  key={agent.id}
                  className={css({
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                    padding: '10px',
                    height: 'auto',
                    ':hover': {
                      backgroundColor: '#3693BF0D',
                    },
                  })}
                >
                  <div
                    className={css({
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    })}
                  >
                    <AgentAvatar
                      name={agent.fullName}
                      variant="small"
                    ></AgentAvatar>
                  </div>
                  <div
                    className={css({
                      display: 'flex',
                      flexGrow: '4',
                      flexDirection: 'column',
                      paddingLeft: '15px',
                      paddingRight: '15px',
                      maxWidth: '300px',
                    })}
                  >
                    <p
                      className={css({
                        fontSize: '17px',
                        fontWeight: '700',
                        color: '#37474F',
                      })}
                    >
                      {agent.fullName}
                    </p>
                    <p
                      className={css({
                        fontSize: '12px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: '#37474F',
                      })}
                    >
                      {agent.emailAddress}
                    </p>
                    <div
                      className={css({
                        display: 'flex',
                        fontSize: '12px',
                        fontWeight: '400',
                        textAlign: 'left',
                        alignItems: 'center',
                        color: '#37474FB2',
                      })}
                    >
                      <span
                        className={css({
                          marginRight: '25px',
                        })}
                      >
                        Status:
                      </span>
                      <Badge
                        color={
                          agent.status === 'active' ? COLOR.positive : COLOR.negative
                        }
                        position="left"
                        status={agent.status}
                      >
                        {agent.status === 'active'
                          ? AgentStatus.Active
                          : AgentStatus.Inactive}
                      </Badge>
                    </div>
                  </div>
                  <div
                    className={css({
                      display: 'flex',
                      alignItems: 'center',
                      flexGrow: 0,
                      flexShrink: 0,
                      flexBasis: 'auto',
                    })}
                  >
                    <Button
                      onClick={() => selectAgent(agent)}
                      disabled={disabledButtonAssignedAgent ? true : false}
                      kind={KIND.secondary}
                      size={SIZE.mini}
                      overrides={{
                        BaseButton: {
                          style: () => ({
                            width: 'auto',
                            height: '40px',
                            borderTopLeftRadius:
                              customTheme.borders.agentInfoButtonBorderRadius,
                            borderBottomLeftRadius:
                              customTheme.borders.agentInfoButtonBorderRadius,
                            borderTopRightRadius:
                              customTheme.borders.agentInfoButtonBorderRadius,
                            borderBottomRightRadius:
                              customTheme.borders.agentInfoButtonBorderRadius,
                            background: '#3693BF',
                            color: '#FFFFFF',
                            paddingLeft: '2rem',
                            paddingRight: '2rem',
                            boxShadow: '0px 0px 5px 0px #3693BF80',
                            ':hover': {
                              backgroundColor: '#f6f6f6',
                              color: '#5192BB',
                            },
                          }),
                        },
                      }}
                    >
                      {variant === 'assign' || variant === 're-assign'
                        ? 'Assign'
                        : 'Invite'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className={css({
            display: 'flex',
            padding: '0px 12px',
            flexDirection: 'column',
            marginTop: '20px',
          })}
        >
          <AgentCountMessage message="No Agents found" />
        </div>
      )}
    </div>
  );
};
